const parseModuleList = (value) => {
	if (!value) return null;
	if (Array.isArray(value)) return value;
	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed : null;
		} catch {
			return null;
		}
	}
	return null;
};

export const CONTACT_CCR_MODULE_KEY = "ITEMS_TEXT.CONTACT_CCR";

export const ADMIN_MODULES = [
	{ key: "ITEMS_TEXT.ORDER_ANALYTICS", group: "Analytics", permissionKeys: ["analytics.view_order_analytics"] },
	{ key: "ITEMS_TEXT.USER_ANALYTICS", group: "Analytics", permissionKeys: ["analytics.view_user_analytics"] },
	{ key: "ITEMS_TEXT.USERS", group: "User & Access", permissionCategories: ["user_management"] },
	{ key: "ITEMS_TEXT.ROLES", group: "User & Access", permissionCategories: ["role_management"] },
	{ key: "ITEMS_TEXT.SITES", group: "User & Access", permissionCategories: ["site_management"] },
	{ key: "ITEMS_TEXT.BANNERS", group: "Content & Communication", labelKey: "ITEMS_TEXT.BANNERS", permissionCategories: ["banner_management"] },
	{ key: "ITEMS_TEXT.DOCUMENTS", group: "Content & Communication", permissionCategories: ["document_management"] },
	{ key: CONTACT_CCR_MODULE_KEY, label: "Contact CCR", group: "Content & Communication" },
	{ key: "ITEMS_TEXT.AMF_METHOD_DOCUMENTATION", group: "Content & Communication", permissionCategories: ["document_management"] },
	{ key: "ITEMS_TEXT.MACHINE_PROVISION", group: "Machine & Product Admin", permissionCategories: ["machine_provision_management"] },
	{ key: "ITEMS_TEXT.PRICE_MARKUPS", group: "Commerce", permissionKeys: ["app_settings.manage_price_markups"] },
	{ key: "ITEMS_TEXT.LINE_DISCOUNTS", group: "Commerce", permissionCategories: ["line_discount_management"], permissionKeys: ["app_settings.manage_line_discounts"] },
	{ key: "ITEMS_TEXT.PROMOTIONS", group: "Commerce", permissionCategories: ["promotion_management"], permissionKeys: ["app_settings.manage_promotions"] },
	{ key: "ITEMS_TEXT.CHIP_REVIEW_APPROVAL", group: "Content & Communication", permissionKeys: ["chip_governance.manage_chip_rules"] },
	{ key: "ITEMS_TEXT.UPLOAD_MODELS", group: "Machine & Product Admin", permissionKeys: ["app_settings.allow_model_uploads"] },
	{
		key: "ITEMS_TEXT.SETTINGS",
		group: "Commerce",
		permissionKeys: ["app_settings.manage_settings", "app_settings.manage_default_values"],
	},
	{ key: "ITEMS_TEXT.POLICY_CENTER", group: "Content & Communication", permissionKeys: ["app_settings.manage_policy_center"] },
	{ key: "ITEMS_TEXT.COMPANY_INFORMATION", group: "System", permissionKeys: ["app_settings.manage_company_info"] },
	{ key: "ITEMS_TEXT.THEME", group: "System", permissionKeys: ["app_settings.manage_theme"] },
	{
		key: "ITEMS_TEXT.SCHEDULER",
		group: "Orders",
		permissionKeys: [
			"order_management.create_order_schedules",
			"order_management.edit_order_schedules",
			"order_management.delete_order_schedules",
		],
	},
	{ key: "ITEMS_TEXT.ALL_PRODUCTS", group: "Machine & Product Admin" },
	{ key: "ITEMS_TEXT.ALL_CUSTOMERS", group: "Machine & Product Admin" },
	{ key: "ITEMS_TEXT.ALL_SITES", group: "Machine & Product Admin" },
	{ key: "ITEMS_TEXT.ALL_MACHINES", group: "Machine & Product Admin" },
	{ key: "ITEMS_TEXT.VERSION_INFO", group: "System" },
];

export const ADMIN_MODULE_KEYS = ADMIN_MODULES.map((module) => module.key);

export const resolveEnabledAdminModules = (optionKeys) => {
    const parsed = parseModuleList(optionKeys?.adminEnabledModules);
    if (parsed) {
        // Ensure AMF_METHOD_DOCUMENTATION is always included
        const withAMF = new Set(parsed);
        if (withAMF.has("ITEMS_TEXT.ANALYTICS")) {
            withAMF.add("ITEMS_TEXT.ORDER_ANALYTICS");
            withAMF.add("ITEMS_TEXT.USER_ANALYTICS");
            withAMF.delete("ITEMS_TEXT.ANALYTICS");
        }
        withAMF.add("ITEMS_TEXT.AMF_METHOD_DOCUMENTATION");
        // ITEMS_TEXT.RECOMMENDATION_ACCEPTANCE used to be force-added here, on the same reasoning as
        // AMF_METHOD_DOCUMENTATION above: a tenant's saved list is a snapshot from whenever it was
        // last written, so a module added to ADMIN_MODULES afterwards would be invisible until
        // somebody re-saved Settings. That made the module's own switch in
        // SuperAdmin > Additional Settings > Admin Module availability INERT — unticking it wrote a
        // list without the key, and the next read put the key straight back, so the tab could never
        // be turned off. The one-time visibility problem is spent now that the list has been saved
        // with the key in it, and a switch that silently does nothing is the worse fault.
        return Array.from(withAMF).filter((key) => ADMIN_MODULE_KEYS.includes(key));
    }
    return ADMIN_MODULE_KEYS;
};
export const isAdminModuleEnabled = (key, enabledSet) => enabledSet.has(key);

export const isPermissionHiddenByModule = (category, permissionKey, enabledSet, isSuperAdmin = false) => {
	if (isSuperAdmin) return false;
	const fullKey = permissionKey ? `${category}.${permissionKey}` : null;
	return ADMIN_MODULES.some((module) => {
		if (isAdminModuleEnabled(module.key, enabledSet)) return false;
		if (module.permissionCategories?.includes(category)) return true;
		if (fullKey && module.permissionKeys?.includes(fullKey)) return true;
		return false;
	});
};

export const isCategoryHiddenByModule = (category, enabledSet, isSuperAdmin = false) =>
	isSuperAdmin
		? false
		: ADMIN_MODULES.some((module) => {
		if (isAdminModuleEnabled(module.key, enabledSet)) return false;
		return module.permissionCategories?.includes(category);
	});
